import { useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Bell,
  Blocks,
  CalendarClock,
  ChartNoAxesCombined,
  ChevronDown,
  CircleDollarSign,
  CloudUpload,
  FileClock,
  GalleryHorizontalEnd,
  Image,
  LayoutDashboard,
  Mail,
  Menu,
  MonitorSmartphone,
  Moon,
  Palette,
  PlaySquare,
  ServerCog,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Sun,
  TextQuote,
  Users,
  X,
} from 'lucide-react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useDirection } from '@/context/direction-provider'
import { useTheme } from '@/context/theme-provider'
import { Button } from '@/components/ui/button'
import { SkipToMain } from '@/components/skip-to-main'
import { api } from './api'
import { useAuth } from './auth'
import { DotItBrand } from './brand'
import { DEFAULT_TENANT_BRAND_COLOR, readableBrandForeground } from './branding'
import type { TenantRole } from './types'
import { StatusPill } from './ui'

interface NavItem {
  label: string
  to: string
  icon: typeof LayoutDashboard
  roles?: TenantRole[]
  platform?: boolean
}

const contentRoles: TenantRole[] = [
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'CONTENT_MANAGER',
]
const deviceRoles: TenantRole[] = [
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'DEVICE_MANAGER',
]
const adminRoles: TenantRole[] = ['TENANT_OWNER', 'TENANT_ADMIN']

const nav: { label: string; items: NavItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: '/app', icon: LayoutDashboard },
      { label: 'Analytics', to: '/app/analytics', icon: ChartNoAxesCombined },
    ],
  },
  {
    label: 'Content',
    items: [
      { label: 'Media', to: '/app/media', icon: Image, roles: contentRoles },
      {
        label: 'Slides',
        to: '/app/slides',
        icon: Blocks,
        roles: contentRoles,
      },
      {
        label: 'Overlays',
        to: '/app/overlays',
        icon: GalleryHorizontalEnd,
        roles: contentRoles,
      },
      {
        label: 'Tickers',
        to: '/app/tickers',
        icon: TextQuote,
        roles: contentRoles,
      },
      {
        label: 'Playlists',
        to: '/app/playlists',
        icon: PlaySquare,
        roles: contentRoles,
      },
      {
        label: 'Schedules',
        to: '/app/schedules',
        icon: CalendarClock,
        roles: contentRoles,
      },
      {
        label: 'Publish',
        to: '/app/publish',
        icon: CloudUpload,
        roles: contentRoles,
      },
    ],
  },
  {
    label: 'Fleet',
    items: [
      {
        label: 'Devices',
        to: '/app/devices',
        icon: MonitorSmartphone,
        roles: deviceRoles,
      },
      {
        label: 'Groups',
        to: '/app/groups',
        icon: GalleryHorizontalEnd,
        roles: deviceRoles,
      },
    ],
  },
  {
    label: 'Organization',
    items: [
      { label: 'Team', to: '/app/team', icon: Users, roles: adminRoles },
      {
        label: 'Billing',
        to: '/app/billing',
        icon: CircleDollarSign,
        roles: ['TENANT_OWNER'],
      },
      {
        label: 'Branding',
        to: '/app/branding',
        icon: Palette,
        roles: adminRoles,
      },
      { label: 'Notifications', to: '/app/notifications', icon: Bell },
      { label: 'Audit log', to: '/app/audit', icon: FileClock },
    ],
  },
  {
    label: 'Platform',
    items: [
      {
        label: 'Tenants',
        to: '/app/platform/tenants',
        icon: ShieldCheck,
        platform: true,
      },
      {
        label: 'Plans',
        to: '/app/platform/plans',
        icon: Settings2,
        platform: true,
      },
      {
        label: 'Email gateway',
        to: '/app/platform/email-gateway',
        icon: Mail,
        platform: true,
      },
      {
        label: 'APK releases',
        to: '/app/platform/releases',
        icon: ServerCog,
        platform: true,
      },
      {
        label: 'System health',
        to: '/app/platform/health',
        icon: Activity,
        platform: true,
      },
    ],
  },
]

interface BillingSummary {
  status: string
  readOnly: boolean
  graceEndsAt: string | null
}

interface OrganizationBranding {
  name: string
  logoUrl: string | null
  primaryColor: string | null
}

function Sidebar({
  open,
  close,
  organization,
}: {
  open: boolean
  close: () => void
  organization?: OrganizationBranding
}) {
  const { session, role } = useAuth()
  return (
    <>
      {open && (
        <button
          className='fixed inset-0 z-40 bg-black/50 lg:hidden'
          aria-label='Close navigation'
          onClick={close}
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 start-0 z-50 flex w-72 flex-col border-e bg-zinc-950 text-zinc-300 transition-transform lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        )}
      >
        <div className='flex h-20 items-center justify-between px-5'>
          <DotItBrand className='[&_span]:text-zinc-50' />
          <Button
            className='lg:hidden'
            size='icon'
            variant='ghost'
            onClick={close}
            aria-label='Close navigation'
          >
            <X className='size-5' />
          </Button>
        </div>
        <nav
          className='min-h-0 flex-1 overflow-y-auto px-3 pb-5'
          aria-label='Main navigation'
        >
          {nav.map((group) => {
            const visible = group.items.filter((item) => {
              if (item.platform) return session?.user.platformAdmin
              return !item.roles || (role && item.roles.includes(role))
            })
            if (visible.length === 0) return null
            return (
              <div key={group.label} className='mb-5'>
                <p className='mb-2 px-3 text-[10px] font-bold tracking-[0.18em] text-zinc-600 uppercase'>
                  {group.label}
                </p>
                <div className='space-y-1'>
                  {visible.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/app'}
                      onClick={close}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-[var(--tenant-brand)] text-[var(--tenant-brand-foreground)] shadow-sm'
                            : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                        )
                      }
                    >
                      <item.icon className='size-[18px]' />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )
          })}
        </nav>
        <div className='border-t border-white/10 p-4'>
          <div className='flex items-center gap-3'>
            {organization?.logoUrl ? (
              <img
                src={organization.logoUrl}
                alt={`${organization.name} logo`}
                className='size-10 rounded-lg bg-white object-contain p-1'
              />
            ) : (
              <span
                className='grid size-10 shrink-0 place-items-center rounded-lg bg-[var(--tenant-brand)] text-sm font-black text-[var(--tenant-brand-foreground)]'
                aria-hidden='true'
              >
                {(organization?.name ?? session?.user.name ?? 'D')
                  .slice(0, 1)
                  .toUpperCase()}
              </span>
            )}
            <div className='min-w-0'>
              <p className='truncate text-xs font-medium text-zinc-200'>
                {organization?.name ?? session?.user.name}
              </p>
              <p className='mt-1 truncate text-xs text-zinc-500'>
                {session?.user.email}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [endingPlatformAccess, setEndingPlatformAccess] = useState(false)
  const { session, logout, switchTenant, exitPlatformAccess } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const { dir, setDir } = useDirection()
  const navigate = useNavigate()
  const billing = useQuery({
    queryKey: ['billing-summary', session?.activeTenantId],
    queryFn: () => api<BillingSummary>('/billing/summary'),
    enabled: Boolean(session?.activeTenantId),
  })
  const organization = useQuery({
    queryKey: ['organization', session?.activeTenantId],
    queryFn: () => api<OrganizationBranding>('/organization'),
    enabled: Boolean(session?.activeTenantId),
  })
  const brandColor =
    organization.data?.primaryColor ?? DEFAULT_TENANT_BRAND_COLOR
  const brandForeground = readableBrandForeground(brandColor)
  const brandStyle = {
    '--primary': brandColor,
    '--primary-foreground': brandForeground,
    '--ring': brandColor,
    '--tenant-brand': brandColor,
    '--tenant-brand-foreground': brandForeground,
  } as CSSProperties
  const activeMembership = session?.memberships.find(
    (membership) => membership.tenantId === session.activeTenantId
  )
  const platformAccessHasMembership = session?.memberships.some(
    (membership) => membership.tenantId === session.platformAccess?.tenantId
  )
  const endPlatformAccess = async () => {
    setEndingPlatformAccess(true)
    try {
      await exitPlatformAccess()
      navigate('/app/platform/tenants')
      toast.success('Platform tenant access ended.')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Unable to end tenant access.'
      )
    } finally {
      setEndingPlatformAccess(false)
    }
  }
  return (
    <div
      className='min-h-svh bg-muted/20'
      data-tenant-brand-color={brandColor}
      style={brandStyle}
    >
      <SkipToMain />
      <Sidebar
        open={mobileOpen}
        close={() => setMobileOpen(false)}
        organization={organization.data}
      />
      <div className='lg:ps-72'>
        <header className='sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur sm:px-6'>
          <Button
            className='lg:hidden'
            size='icon'
            variant='ghost'
            onClick={() => setMobileOpen(true)}
            aria-label='Open navigation'
          >
            <Menu className='size-5' />
          </Button>
          <div className='relative'>
            <label className='sr-only' htmlFor='tenant-switcher'>
              Organization
            </label>
            <select
              id='tenant-switcher'
              className='h-9 appearance-none rounded-lg border bg-background ps-3 pe-8 text-sm font-medium'
              value={session?.activeTenantId ?? ''}
              onChange={(event) => void switchTenant(event.target.value)}
            >
              {session?.platformAccess && !platformAccessHasMembership && (
                <option value={session.platformAccess.tenantId}>
                  {session.platformAccess.tenantName} · PLATFORM ACCESS
                </option>
              )}
              {session?.memberships.map((membership) => (
                <option key={membership.tenantId} value={membership.tenantId}>
                  {membership.tenantId === session.activeTenantId
                    ? session.platformAccess
                      ? `${session.platformAccess.tenantName} · PLATFORM ACCESS`
                      : `Workspace · ${activeMembership?.role.replaceAll('_', ' ')}`
                    : membership.tenantId.slice(0, 8)}
                </option>
              ))}
            </select>
            <ChevronDown className='pointer-events-none absolute end-2 top-2.5 size-4 text-muted-foreground' />
          </div>
          <div className='ms-auto flex items-center gap-1'>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => setDir(dir === 'ltr' ? 'rtl' : 'ltr')}
              aria-label='Toggle text direction'
            >
              <span className='text-xs font-black'>
                {dir === 'ltr' ? 'RTL' : 'LTR'}
              </span>
            </Button>
            <Button
              size='icon'
              variant='ghost'
              onClick={() =>
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
              }
              aria-label='Toggle color theme'
            >
              {resolvedTheme === 'dark' ? (
                <Sun className='size-4' />
              ) : (
                <Moon className='size-4' />
              )}
            </Button>
            <Button
              variant='ghost'
              className='ms-1 hidden sm:inline-flex'
              onClick={() => void logout().then(() => navigate('/login'))}
            >
              Sign out
            </Button>
          </div>
        </header>
        {session?.platformAccess && (
          <div className='flex flex-wrap items-center justify-center gap-3 border-b border-orange-500/40 bg-orange-500/15 px-4 py-2.5 text-center text-xs text-orange-950 dark:text-orange-200'>
            <ShieldAlert className='size-4 shrink-0' />
            <strong>
              Platform access: {session.platformAccess.tenantName}
            </strong>
            <StatusPill
              value={session.platformAccess.scope}
              tone={
                session.platformAccess.scope === 'READ_ONLY'
                  ? 'neutral'
                  : 'warning'
              }
            />
            <span>
              Expires{' '}
              {new Date(session.platformAccess.expiresAt).toLocaleString()}
            </span>
            <Button
              size='sm'
              variant='outline'
              disabled={endingPlatformAccess}
              onClick={() => void endPlatformAccess()}
            >
              {endingPlatformAccess ? 'Exiting…' : 'Exit tenant'}
            </Button>
          </div>
        )}
        {billing.data?.readOnly && (
          <div className='flex flex-wrap items-center justify-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-center text-xs text-amber-800 dark:text-amber-300'>
            <StatusPill value={billing.data.status} tone='warning' />
            Content changes are paused until billing is restored.
            {billing.data.graceEndsAt && (
              <span>
                Players keep their last publication until{' '}
                {new Date(billing.data.graceEndsAt).toLocaleDateString()}.
              </span>
            )}
            <NavLink className='font-bold underline' to='/app/billing'>
              Resolve billing
            </NavLink>
          </div>
        )}
        <main
          id='main-content'
          className='mx-auto w-full max-w-[1600px] p-4 sm:p-6 lg:p-8'
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
